import React from 'react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">ALLARTWEB TERMS AND CONDITIONS</h1>
        <p className="text-gray-600 mb-8">Last Updated: November 2025</p>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction & Definitions</h2>
            <p>Welcome to AllArtWeb ("Platform", "Service", "we", "us", or "our"). These Terms and Conditions ("Terms") govern your access to and use of our e-commerce platform operating in Kenya and East Africa.</p>
            <p className="mt-4"><strong>Key Definitions:</strong></p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li><strong>User:</strong> Any individual or entity accessing our platform</li>
              <li><strong>Buyer:</strong> Users who purchase products</li>
              <li><strong>Seller/Vendor:</strong> Users who list and sell products</li>
              <li><strong>Platform:</strong> AllArtWeb website and mobile application</li>
              <li><strong>Content:</strong> Text, images, videos, product listings, reviews</li>
              <li><strong>Transaction:</strong> Purchase of products through our platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Eligibility</h2>
            <p>By using AllArtWeb, you represent and warrant that:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>You are at least 18 years old</li>
              <li>You are a resident of Kenya, Uganda, Tanzania, Rwanda, or other East African countries where we operate</li>
              <li>You have the legal capacity to enter into binding agreements</li>
              <li>You are not prohibited from using this platform under applicable laws</li>
              <li>All information you provide is accurate and truthful</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <p>To access certain features, you must create an account. You agree to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the confidentiality of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of unauthorized access</li>
              <li>Not create multiple accounts for fraudulent purposes</li>
              <li>Comply with all applicable laws in your jurisdiction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Conduct</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Post offensive, defamatory, or threatening content</li>
              <li>Engage in fraud, misrepresentation, or deceptive practices</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Harass, abuse, or discriminate against other users</li>
              <li>Post spam or repetitive advertising</li>
              <li>Use automated bots or scrapers without permission</li>
              <li>Interfere with platform operations or security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Seller Obligations</h2>
            <p>If you are a seller, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Provide accurate product descriptions and images</li>
              <li>Price items fairly without predatory markups</li>
              <li>Not sell prohibited items (counterfeit, dangerous, illegal goods)</li>
              <li>Fulfill orders within agreed timeframes</li>
              <li>Maintain quality standards for all products</li>
              <li>Respond to customer inquiries within 24 hours</li>
              <li>Comply with all product safety regulations</li>
              <li>Obtain necessary licenses and permits</li>
              <li>Maintain accurate inventory counts</li>
              <li>Honor the platform's return and refund policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Buyer Obligations</h2>
            <p>If you are a buyer, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Provide accurate delivery information</li>
              <li>Pay for items through approved payment methods</li>
              <li>Not attempt payment fraud or chargebacks</li>
              <li>Inspect products upon delivery</li>
              <li>Provide honest product reviews</li>
              <li>Not abuse return and refund policies</li>
              <li>Report issues within specified timeframes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payments & Fees</h2>
            <p>Payment Details:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li><strong>Currency:</strong> All prices are in Kenyan Shillings (KES)</li>
              <li><strong>Accepted Methods:</strong> M-Pesa, Bank Transfer, Credit/Debit Card, PayPal</li>
              <li><strong>Escrow Model:</strong> Funds held until delivery confirmation</li>
              <li><strong>Service Fee:</strong> 3-5% platform commission on seller transactions</li>
              <li><strong>Payment Processing:</strong> Immediate upon transaction completion</li>
              <li><strong>Taxes:</strong> Included in displayed prices where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Shipping & Delivery</h2>
            <p>We facilitate logistics partnerships for delivery:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Sellers arrange and pay for shipping</li>
              <li>Delivery times are estimates, not guarantees</li>
              <li>Tracking information provided where available</li>
              <li>Risk transfers to buyer upon delivery attempt</li>
              <li>Lost/damaged items subject to insurance claims</li>
              <li>International shipping follows customs regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Returns & Refunds</h2>
            <p>Return Policy:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li><strong>Return Window:</strong> 14 days from delivery</li>
              <li><strong>Conditions:</strong> Item must be unused in original packaging</li>
              <li><strong>Return Shipping:</strong> Buyer covers return costs unless seller error</li>
              <li><strong>Refund Timeline:</strong> 5-10 business days after verification</li>
              <li><strong>Exceptions:</strong> Final sale items clearly marked</li>
              <li><strong>Defective Items:</strong> Full refund or replacement</li>
              <li><strong>Missing Items:</strong> Refund issued if not delivered</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Intellectual Property Rights</h2>
            <p>All platform content including logos, design, text is owned by AllArtWeb. Users retain rights to their uploaded content but grant AllArtWeb a non-exclusive, royalty-free license to use, display, and distribute content on the platform.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Privacy & Data Protection</h2>
            <p>We comply with Kenya's Data Protection Act and handle personal information according to our Privacy Policy:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Personal data used solely for transaction and service purposes</li>
              <li>Data not sold to third parties</li>
              <li>Encryption used for sensitive information</li>
              <li>Users can request data deletion within legal limits</li>
              <li>Cookies used for platform optimization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Dispute Resolution</h2>
            <p>Process for resolving disputes:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li><strong>Step 1:</strong> Attempt negotiation between parties</li>
              <li><strong>Step 2:</strong> Platform mediation if no resolution</li>
              <li><strong>Step 3:</strong> Binding arbitration in Nairobi</li>
              <li><strong>Arbitration:</strong> Under Kenya Arbitration Act</li>
              <li><strong>Legal Venue:</strong> High Court of Kenya (if arbitration fails)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, AllArtWeb shall not be liable for:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Indirect, incidental, or consequential damages</li>
              <li>Lost profits or business interruption</li>
              <li>Product quality issues from sellers</li>
              <li>Third-party platform or payment processor failures</li>
              <li>Force majeure events beyond our control</li>
            </ul>
            <p className="mt-4">Maximum liability: Refund of amount paid for specific transaction</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Indemnification</h2>
            <p>You agree to indemnify and hold harmless AllArtWeb from any claims, damages, or losses arising from:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Your violation of these Terms</li>
              <li>Your use of the platform in violation of laws</li>
              <li>Your infringement of third-party rights</li>
              <li>Your dispute with other users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Termination</h2>
            <p>AllArtWeb may terminate or suspend your account if you:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Violate any provision of these Terms</li>
              <li>Engage in fraudulent activity</li>
              <li>Repeatedly violate platform policies</li>
              <li>Have multiple customer complaints</li>
            </ul>
            <p className="mt-4">You may terminate your account at any time through account settings.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Modifications to Terms</h2>
            <p>We may update these Terms at any time. Continued use of the platform after modifications constitutes acceptance of updated Terms. We will notify users of significant changes.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Force Majeure</h2>
            <p>AllArtWeb is not liable for delays or failures due to unforeseen circumstances including natural disasters, wars, pandemics, or government actions.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Consumer Protection Act Compliance</h2>
            <p>These Terms comply with Kenya's Consumer Protection Act (2012), including:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Fair trading and honest dealing obligations</li>
              <li>Product quality and safety standards</li>
              <li>Consumer dispute resolution mechanisms</li>
              <li>Right to fair compensation</li>
              <li>Protection against misleading advertising</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Contact Information</h2>
            <p>For questions about these Terms, contact:</p>
            <p className="mt-4">
              <strong>AllArtWeb Support</strong><br />
              Email: support@allartweb.com<br />
              Phone: +254 (Contact Number)<br />
              Address: Nairobi, Kenya<br />
              Website: www.allartweb.com
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Severability</h2>
            <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions continue in full effect.</p>
          </section>

          <div className="bg-gray-100 p-6 rounded-lg mt-12">
            <p className="text-sm text-gray-600">
              By using AllArtWeb, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
